/**
 * Presentation-only track scene manifest written by `npm run scene:import`.
 * It never affects map hashes, physics or replays; see
 * Docs/planned_features/track_scene_export/.
 */
export interface TrackVisualGroundVariant {
  file: string;
  width: number;
  height: number;
  sha256: string;
}

export interface TrackVisualLighting {
  /** Unit vector in runtime axes pointing from the scene toward the sun. */
  sunDirection: [number, number, number];
  sunIntensity: number;
  sunColor: [number, number, number];
  ambientSky: [number, number, number];
  ambientIntensity: number;
}

export interface TrackVisualManifestV1 {
  schemaVersion: 1;
  trackId: number;
  visualRevision: number;
  mapWidth: number;
  mapHeight: number;
  referenceMapHash: string;
  pixelsPerUnit: number;
  origin: 'center';
  axisConvention: 'gltf-y-up';
  /** Largest first; the runtime picks the first that fits the GPU. */
  ground: TrackVisualGroundVariant[];
  scenery: { file: string; sha256: string; triangles: number; requiredExtensions: string[] };
  lighting: TrackVisualLighting;
  maxHeight: number;
}

type Json = Record<string, unknown>;

function isRecord(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function object(value: unknown, field: string): Json {
  if (!isRecord(value)) throw new Error(`Track visual manifest: ${field} must be an object`);
  return value;
}

function finite(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value))
    throw new Error(`Track visual manifest: ${field} must be a finite number`);
  return value;
}

function positiveInteger(value: unknown, field: string): number {
  const number = finite(value, field);
  if (!Number.isInteger(number) || number <= 0)
    throw new Error(`Track visual manifest: ${field} must be a positive integer`);
  return number;
}

function nonNegative(value: unknown, field: string): number {
  const number = finite(value, field);
  if (number < 0) throw new Error(`Track visual manifest: ${field} must not be negative`);
  return number;
}

function hash(value: unknown, field: string): string {
  if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value))
    throw new Error(`Track visual manifest: ${field} must be a SHA-256 hex digest`);
  return value;
}

function fileName(value: unknown, field: string): string {
  if (typeof value !== 'string' || !/^[\w@.-]+$/.test(value) || value.startsWith('.'))
    throw new Error(`Track visual manifest: ${field} must be a plain file name`);
  return value;
}

function vector3(value: unknown, field: string): [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3)
    throw new Error(`Track visual manifest: ${field} must have three numbers`);
  return [finite(value[0], field), finite(value[1], field), finite(value[2], field)];
}

export function parseTrackVisualManifest(value: unknown): TrackVisualManifestV1 {
  const root = object(value, 'manifest');
  if (root.schemaVersion !== 1) throw new Error('Track visual manifest: unsupported schemaVersion');
  if (root.origin !== 'center') throw new Error('Track visual manifest: unsupported origin');
  if (root.axisConvention !== 'gltf-y-up')
    throw new Error('Track visual manifest: unsupported axisConvention');

  if (!Array.isArray(root.ground) || root.ground.length === 0)
    throw new Error('Track visual manifest: ground must list at least one image');
  const ground = root.ground.map((entry: unknown, index) => {
    const variant = object(entry, `ground[${index}]`);
    return {
      file: fileName(variant.file, `ground[${index}].file`),
      width: positiveInteger(variant.width, `ground[${index}].width`),
      height: positiveInteger(variant.height, `ground[${index}].height`),
      sha256: hash(variant.sha256, `ground[${index}].sha256`),
    };
  });

  const scenery = object(root.scenery, 'scenery');
  const required = scenery.requiredExtensions;
  // GLTFLoader handles WebP natively, and the ground already requires browser WebP support.
  // Keep decoder-dependent extensions (Draco, KTX2, etc.) out of this allowlist.
  if (!Array.isArray(required))
    throw new Error('Track visual manifest: scenery requires unsupported glTF extensions');
  const requiredExtensions = required.map((extension: unknown) => {
    if (extension !== 'EXT_texture_webp')
      throw new Error('Track visual manifest: scenery requires unsupported glTF extensions');
    return extension;
  });

  const lighting = object(root.lighting, 'lighting');
  const sunDirection = vector3(lighting.sunDirection, 'lighting.sunDirection');
  if (Math.abs(Math.hypot(...sunDirection) - 1) > 1e-3)
    throw new Error('Track visual manifest: lighting.sunDirection must be a unit vector');

  return {
    schemaVersion: 1,
    trackId: positiveInteger(root.trackId, 'trackId'),
    visualRevision: positiveInteger(root.visualRevision, 'visualRevision'),
    mapWidth: positiveInteger(root.mapWidth, 'mapWidth'),
    mapHeight: positiveInteger(root.mapHeight, 'mapHeight'),
    referenceMapHash: hash(root.referenceMapHash, 'referenceMapHash'),
    pixelsPerUnit: positiveInteger(root.pixelsPerUnit, 'pixelsPerUnit'),
    origin: 'center',
    axisConvention: 'gltf-y-up',
    ground,
    scenery: {
      file: fileName(scenery.file, 'scenery.file'),
      sha256: hash(scenery.sha256, 'scenery.sha256'),
      triangles: positiveInteger(scenery.triangles, 'scenery.triangles'),
      requiredExtensions,
    },
    lighting: {
      sunDirection,
      sunIntensity: nonNegative(lighting.sunIntensity, 'lighting.sunIntensity'),
      sunColor: vector3(lighting.sunColor, 'lighting.sunColor'),
      ambientSky: vector3(lighting.ambientSky, 'lighting.ambientSky'),
      ambientIntensity: nonNegative(lighting.ambientIntensity, 'lighting.ambientIntensity'),
    },
    maxHeight: nonNegative(root.maxHeight, 'maxHeight'),
  };
}

export function selectGroundVariant(
  manifest: TrackVisualManifestV1,
  maxTextureSize: number,
): TrackVisualGroundVariant | null {
  return (
    manifest.ground.find((variant) => Math.max(variant.width, variant.height) <= maxTextureSize) ??
    null
  );
}

/** glTF Y-up scene units -> runtime map pixels (X right, Y height, Z map-down). */
export function sceneRootTransform(manifest: TrackVisualManifestV1): {
  scale: number;
  position: [number, number, number];
} {
  return {
    scale: manifest.pixelsPerUnit,
    position: [manifest.mapWidth / 2, 0, manifest.mapHeight / 2],
  };
}
